import { BackLink, FaqComponent, Icon } from '@perch-labs/shrike';
import { useRef } from 'react';
import googleIcon from 'static/google-icon.svg';
import { Button } from '~/components/button';
import { FaqButton } from '~/components/button/button.style';
import { Wrapper } from '~/components/customer-details/customer-details.style';
import { COMMUNITY_SOLAR, DialogSelector } from '~/components/dialogs';
import { Disclaimer } from '~/components/disclaimer';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import {
  type ImageType,
  ImageSelector,
  CREATE_ACCOUNT_IMAGE,
  GLOBE_HOME,
} from '~/components/image';
import { Inputs } from '~/components/inputs';
import { DisabledInput } from '~/components/inputs/disabled-input';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { GeneralTerms } from '~/components/terms/general-terms';
import { Text, Title, Description } from '~/components/typography';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { MINNESOTA_LMI_FUNNEL_PATH } from '~/funnels/minnesota-lmi/config';
import { SOLAR_LANDSCAPE_FUNNEL_ID } from '~/funnels/solar-landscape/config';
import { useCopy, useForm } from '~/hooks';
import { Utility } from '~/typings/context';
import { FunnelTrack } from '~/typings/graphql';
import { getSavingsRateDescription } from '~/utils/funnels';
import { TRACK } from '~/utils/tracking';
import { minnesotaLmiCopy } from '../create-account/create-account.component';
import { OauthMessage } from './create-account-google.style';
import { faqData } from './faq.data';

export const CreateAccountGoogle = ({
  context,
  error,
  flags,
  goTo,
  next,
  overrideCopy,
  pending,
}: CreateAccountGoogleProps) => {
  const showPhone = !!(flags?.showPhone || flags?.requirePhone);
  const showBusinessName = !!flags?.requireBusinessName;
  const showInviteCode = !!flags?.showInviteCode || !!context.inviteCode;

  const isMD = context.utility.state.abbreviation === 'MD';
  const isSolarLandscape = context.funnelId === SOLAR_LANDSCAPE_FUNNEL_ID;
  const isGoSolar = context.funnelId === GO_SOLAR_FUNNEL_ID;
  const isCommunitySolar = context.funnelId === COMMUNITY_SOLAR_FUNNEL_ID;
  const showLMISavingsMessage = () => {
    return isSolarLandscape || (isMD && (isGoSolar || isCommunitySolar));
  };

  const copy = useCopy(context, checkCopy, overrideCopy);
  const faqRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  const [form, updateForm, isValid] = useForm({
    email: context.googleUser?.email,
    firstName: context.googleUser?.firstName,
    generalContractIds: [],
    lastName: context.googleUser?.lastName,
    ...(showPhone && { phone: context.phone }),
    ...(showPhone && flags.shouldShowOptInSMSOption && { optInToSMS: context.optInToSMS }),
    ...(showBusinessName && { businessName: context.businessName }),
    ...(showInviteCode && { inviteCode: context.inviteCode }),
  });

  return (
    <Wrapper ref={backToTopRef} style={{ border: 'none' }}>
      <TwoColumnGrid testId="screen-CreateAccountGoogle">
        {!flags?.hideBackLink ? (
          <TwoColumnGrid.Navigation>
            <BackLink openInNewTab={false} onClick={goTo?.utility} data-testid="utility-backlink">
              Back
            </BackLink>
          </TwoColumnGrid.Navigation>
        ) : null}
        <TwoColumnGrid.Primary>
          {!!flags?.showStep && (
            <Text color="secondary" fontWeight="700" fontSize="16px">
              {copy.step}
            </Text>
          )}
          <Title>{copy.title}</Title>
          <Description mb={5}>{copy.description}</Description>
          {!!copy.descriptionTwo && <Description mb={5}>{copy.descriptionTwo}</Description>}
          {!flags?.hideLearnMoreDialogSelector ? (
            <Description mb={6}>
              <Icon icon="QuestionCircleInverted" margin={{ bottom: '2px', right: '8px' }} />
              {showLMISavingsMessage() ? (
                <Text fontSize="14px">{copy.lmiSavingsPercentageDescription}</Text>
              ) : null}
              <DialogSelector
                type={copy.learnMoreModal}
                // @ts-expect-error copies are complicatedly typed fixing this error
                // means significant refactoring. TODO.
                copy={copy.learnMoreCopy}
                target={
                  <Link color="primary" track={TRACK.learnMoreCsClicked}>
                    {copy.learnMoreText}
                  </Link>
                }
              />
            </Description>
          ) : null}
          {flags?.showFaqButton ? (
            <FaqButton
              fullWidth={false}
              mode="default"
              onClick={() => {
                faqRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {' '}
              <Icon
                color="primary"
                icon="InfoCircleInverted"
                margin="5px"
                opacity="high"
                path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                scale={2}
                title="InfoCircleInverted"
              />
              View FAQs
            </FaqButton>
          ) : null}
          <Form isValid={isValid} onSubmit={() => next(form as CreateAccountGoogleForm)}>
            <Text tag="h2" textStyle="heading700">
              {copy.titleForm}
            </Text>
            <Container addInputMargin marginY={6}>
              {!flags?.showZipCode ? (
                <DisabledInput testId="utility-name" label="Electric utility">
                  {context.utility.displayName}
                </DisabledInput>
              ) : null}
              <Inputs.Email
                readOnly
                onChange={(
                  _,
                  params: {
                    name: 'email';
                    isValid: boolean;
                  },
                ) => {
                  updateForm(form.email, params); // ensure google email is readonly
                }}
                value={form.email}
              />
              {!!showBusinessName && (
                <Inputs.BusinessName onChange={updateForm} value={form.businessName} />
              )}
              <Inputs.FirstName onChange={updateForm} value={form.firstName} />
              <Inputs.LastName onChange={updateForm} value={form.lastName} />
              {!!showPhone && (
                <Inputs.Phone
                  label="Phone"
                  name="phone"
                  onChange={updateForm}
                  value={form.phone}
                  optional={!flags?.requirePhone}
                />
              )}
              {!!showInviteCode && (
                <Inputs.InviteCode
                  disabled={!!context.inviteCode}
                  onChange={updateForm}
                  value={form.inviteCode}
                />
              )}
              {flags?.showZipCode ? (
                <>
                  <DisabledInput testId="zip-code" label="Zip Code">
                    {context.zipCode}
                  </DisabledInput>
                  <DisabledInput testId="utility-name" label="Electric utility">
                    {context.utility.displayName}
                  </DisabledInput>
                </>
              ) : null}
            </Container>
            {!!flags?.shouldShowOptInSMSOption && (
              <Inputs.OptInToSmsCheckbox checked={form.optInToSMS} onChange={updateForm} />
            )}
            <GeneralTerms onChange={updateForm} />
            <Container mt={6}>
              <ErrorMessage mb={2} error={error} />
              <Button disabled={!isValid} loading={pending} mode="primary" type="submit">
                {copy.next}
              </Button>
            </Container>
          </Form>
          <OauthMessage>
            <img alt="" src={googleIcon} />
            <Text>Signed in with Google</Text>
          </OauthMessage>
          <Disclaimer>{copy.disclaimer}</Disclaimer>
        </TwoColumnGrid.Primary>
        <TwoColumnGrid.Secondary style={{ marginLeft: '160px' }}>
          {flags?.showFaqButton ? (
            <Wrapper
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData} header="Frequently Asked Questions" />
            </Wrapper>
          ) : (
            <ImageSelector type={copy.imageType} />
          )}
        </TwoColumnGrid.Secondary>
        {flags?.showFaqButton ? (
          <TwoColumnGrid.MobileOnlySecondary>
            <Wrapper
              ref={faqRef}
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData} header="Frequently Asked Questions" />
            </Wrapper>
            <Wrapper
              onClick={() => {
                backToTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: ' 2px',
              }}
            >
              <Text textAlign="center" mt={5} paddingBottom={5} textStyle="heading600">
                Back to top
                <Icon
                  color="primary"
                  icon="ArrowUp"
                  margin="5px"
                  opacity="high"
                  path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                  scale={2}
                  title="ArrowUp"
                />
              </Text>
            </Wrapper>
          </TwoColumnGrid.MobileOnlySecondary>
        ) : null}
      </TwoColumnGrid>
    </Wrapper>
  );
};

export const checkCopy = context => {
  if (context.utility.funnelTrack === FunnelTrack.Waitlist) {
    return waitListCopy(context);
  } else if (context.googleUser.redirectPath === MINNESOTA_LMI_FUNNEL_PATH) {
    return {
      ...defaultCopy(context),
      ...minnesotaLmiCopy(),
    };
  } else {
    return defaultCopy(context);
  }
};

export const defaultCopy = ({ utility, zipCode }: CreateAccountGoogleContext) => {
  const savingsRate = getSavingsRateDescription(utility.state.abbreviation);
  return {
    title: `Good news! Community solar is available in your zip code ${zipCode}.`,
    description: `To unlock savings of ${savingsRate} on the solar credits applied to your electric bill, create your Arcadia account so that we can match you with a community solar farm in your area.`,
    descriptionTwo: '',
    next: 'Continue',
    titleForm: 'Confirm your info',
    disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${utility.displayName}. ${utility.displayName} has not endorsed this service. Savings rates vary.`,
    imageType: CREATE_ACCOUNT_IMAGE as ImageType,
    learnMoreText: 'Why join a community solar farm?',
    learnMoreModal: COMMUNITY_SOLAR,
    step: 'Step 1 of 4',
    lmiSavingsPercentageDescription: `*Low-to-moderate income households save${savingsRate}; all others save 5%.`,
  };
};

export const waitListCopy = ({ utility }: CreateAccountGoogleContext) => {
  return {
    title: "Community solar is not yet available in your area, but we're working on it!",
    description:
      "We’re always adding new solar farms. Create your account today and be the first to know when we're available in your area! Once a solar farm is live you'll be able to save up to 10% on your energy costs while supporting clean energy.",
    descriptionTwo:
      "There's no commitment required and no hidden charges. Just give us some basic information about you and your home, and we'll keep you updated on our progress.",
    next: 'Join waitlist',
    titleForm: '',
    imageType: GLOBE_HOME as ImageType,
    disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${utility.displayName}. ${utility.displayName} has not endorsed this service. Savings rates vary.`,
    learnMoreText: 'Why join a community solar farm?',
    learnMoreModal: COMMUNITY_SOLAR,
    step: 'Step 1 of 4',
    lmiSavingsPercentageDescription: '',
  };
};

export type CreateAccountGoogleCopy = ReturnType<typeof defaultCopy>;

export interface CreateAccountGoogleProps {
  context: CreateAccountGoogleContext;
  error?: string;
  goTo?: {
    utility: () => void;
  };
  flags?: {
    requireBusinessName?: boolean;
    requirePhone?: boolean;
    showPhone?: boolean;
    showInviteCode?: boolean;
    hideBackLink?: boolean;
    hideLearnMoreDialogSelector?: boolean;
    showFaqButton?: boolean;
    showZipCode?: boolean;
    showStep?: boolean;
    shouldShowOptInSMSOption?: boolean;
  };
  next: (payload: CreateAccountGoogleForm) => void;
  overrideCopy?: (context: CreateAccountGoogleContext) => Partial<CreateAccountGoogleCopy>;
  pending: boolean;
}

export interface CreateAccountGoogleForm {
  email: string;
  firstName: string;
  generalContractIds?: number[];
  lastName: string;
  businessName?: string;
  phone?: string;
}

export interface CreateAccountGoogleContext {
  businessName?: string;
  googleUser: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  inviteCode?: string;
  phone?: string;
  optInToSMS?: boolean;
  promoCode?: string;
  utility: Utility;
  zipCode: string;
  funnelId?: string;
}
